import axios from "axios";

const BASE = process.env.REACT_APP_BACKEND_URL;

export interface UserStory {
  id: string;
  title: string;
  asA: string;
  iWant: string;
  soThat: string;
  acceptanceCriteria: string[];
  priority: "must" | "should" | "could" | "wont";
  storyPoints?: number;
  status: "backlog" | "in-progress" | "done";
  startDate?: string;
  dueDate?: string;
  estimatedDays?: number;
  dependencies?: string[];
  originalDueDate?: string;
}

export interface AnalysisEntry {
  id: string;
  techniqueId: string;
  techniqueName: string;
  data: Record<string, string | string[]>;
  createdAt: string;
  updatedAt: string;
}

export interface Project {
  id: string;
  name: string;
  description: string;
  diagramText: string;
  userStories: UserStory[];
  analyses: AnalysisEntry[];
  documents: DocumentEntry[];
  stakeholders: Stakeholder[];
  raciMatrix: RaciEntry[];
  createdAt: string;
  updatedAt: string;
  status: "active" | "archived" | "draft";
  methodology: "agile" | "waterfall";
  sprintLength?: number;
}

export interface DocumentEntry {
  id: string;
  type: "brd" | "frd" | "srs" | "notes";
  title: string;
  content: string;
  createdAt: string;
  updatedAt: string;
}

export interface Stakeholder {
  id: string;
  name: string;
  role: string;
  team: string;
  email?: string;
  phone?: string;
  timezone?: string;
  notes?: string;
}

export type RaciType = "R" | "A" | "C" | "I" | "";

export interface RaciEntry {
  id: string;
  activity: string;
  assignments: Record<string, RaciType>;
}

export async function getProjects(): Promise<Project[]> {
  const { data } = await axios.get(`${BASE}/api/projects`);
  return data;
}

export async function getProject(id: string): Promise<Project | undefined> {
  try {
    const { data } = await axios.get(`${BASE}/api/projects/${id}`);
    return data;
  } catch {
    return undefined;
  }
}

export async function createProject(name: string, description: string): Promise<Project> {
  const { data } = await axios.post(`${BASE}/api/projects`, {
    name,
    description,
    diagramText: "",
    userStories: [],
    analyses: [],
    documents: [],
    stakeholders: [],
    raciMatrix: [],
    status: "draft",
    methodology: "agile",
    sprintLength: 2,
  });
  return data;
}

export async function updateProject(
  id: string,
  updates: Partial<Omit<Project, "id" | "createdAt">>
): Promise<Project | undefined> {
  try {
    const { data } = await axios.put(`${BASE}/api/projects/${id}`, updates);
    return data;
  } catch {
    return undefined;
  }
}

export async function deleteProject(id: string): Promise<void> {
  await axios.delete(`${BASE}/api/projects/${id}`);
}
